import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { User, UserPreferences } from '@/types/auth'
import * as authApi from '@/api/auth'
import i18n, { setLocale } from '@/i18n'

// Read the picker's current selection at submit time. Importing the
// i18n instance directly (rather than computing from user.value) is
// load-bearing for the login flow: before login user.value is null,
// so any computed reading user.value would default to 'en' and
// silently overwrite the user's pick on the /login screen.
function currentLocale(): 'en' | 'el' | 'pt-BR' | 'es' {
  const loc = (i18n.global.locale as unknown as { value: string }).value
  if (loc === 'el' || loc === 'pt-BR' || loc === 'es') return loc
  return 'en'
}

// ---------------------------------------------------------------------------
// Session invariants (INV-003, INV-004) -- updated for the LATAM rollout
//
// The User row is the single source of truth for language / unit_system.
// Both fields are mutable from three places, all of which write to the
// same column:
//
//   1. Registration -- sets the initial value.
//   2. POST /auth/login -- writes user.language = body.language. The
//      login picker on /login is therefore authoritative: pick a
//      language, sign in, the row is updated. The frontend MUST send
//      the picker's current selection (i18n.locale.value via
//      currentLocale() below); reading from user.value pre-login
//      defaults to 'en' and silently undoes any /account change.
//   3. PUT /me/preferences -- the live-switch path used by the
//      /account picker via changeLanguage() below. Writes to the DB,
//      calls setLocale() for an immediate UI flip, then refreshes the
//      JWT so the access-token's session_language claim and the DB
//      stay in lockstep. Without the JWT refresh, backend-issued
//      strings (LLM responses, async-enqueued emails) would lag for
//      one access-token TTL.
//
// The /auth/refresh handler always reads session_language from the
// User row -- never from the previous refresh-token payload -- so any
// /account change is visible the very first time the access token
// rotates.
//
// Known acceptance (INV-004 narrowed, not removed): ChatThread rows
// persisted under the prior (user_id, field_id, language, unit_system)
// tuple remain visible only when queried under that tuple. A user who
// changes language sees old conversations "disappear" from view -- they
// are not destroyed, just keyed differently. Dropping language from the
// ChatThread scoping tuple is the eventual fix; until then this
// behaviour is documented and accepted.
//
// Cross-device corner case: a user with stored 'pt-BR' who opens the
// app on a fresh browser sees /login default to 'en' (localStorage is
// empty). If they sign in WITHOUT clicking the picker first, login
// will overwrite their stored language to 'en'. The mitigation is to
// fall back to navigator.language when localStorage is empty -- not
// done yet; tracked separately.
// ---------------------------------------------------------------------------

const SESSION_KEEPALIVE_MS = 10 * 60 * 1000 // 10 minutes

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  let keepAliveTimer: ReturnType<typeof setInterval> | null = null
  const isAuthenticated = computed(() => !!user.value)
  const language = computed(() => user.value?.language ?? (localStorage.getItem('agroptimizer_lang') as 'en' | 'el') ?? 'en')
  const unitSystem = computed(() => user.value?.unit_system ?? (localStorage.getItem('agroptimizer_units') as 'imperial' | 'metric') ?? 'imperial')

  function startSessionKeepAlive() {
    stopSessionKeepAlive()
    keepAliveTimer = setInterval(async () => {
      try {
        await authApi.refreshSession()
      } catch {
        // Silent failure; the 401 interceptor will handle redirect if needed
      }
    }, SESSION_KEEPALIVE_MS)
  }

  function stopSessionKeepAlive() {
    if (keepAliveTimer !== null) {
      clearInterval(keepAliveTimer)
      keepAliveTimer = null
    }
  }

  async function login(email: string, password: string) {
    // The picker on /login mutates i18n.locale + localStorage on
    // click. Read from there at submit time so the body actually
    // carries the user's pick. Reading from `language` (computed off
    // user.value) would default to 'en' because user.value is still
    // null pre-login -- that was the bug that made the picker look
    // dead.
    const lang = currentLocale()
    const units = unitSystem.value
    const session = await authApi.login({ email, password, language: lang, unit_system: units })
    localStorage.setItem('agroptimizer_lang', session.language)
    localStorage.setItem('agroptimizer_units', session.unit_system)
    setLocale(session.language)
    // Fetch full user profile now that httpOnly cookies are set
    user.value = await authApi.getMe()
    if (user.value?.language) {
      setLocale(user.value.language)
    }
    startSessionKeepAlive()
  }

  async function register(email: string, password: string, lang: 'en' | 'el' | 'pt-BR' | 'es', units: 'imperial' | 'metric') {
    const session = await authApi.register({ email, password, language: lang, unit_system: units })
    localStorage.setItem('agroptimizer_lang', session.language)
    localStorage.setItem('agroptimizer_units', session.unit_system)
    setLocale(session.language)
    user.value = await authApi.getMe()
    if (user.value?.language) {
      setLocale(user.value.language)
    }
    startSessionKeepAlive()
  }

  async function logout() {
    stopSessionKeepAlive()
    try { await authApi.logout() } catch { /* ignore */ }
    user.value = null

    // Clear cross-user simulation state: another user signing in on the
    // same browser must not inherit cached runs / in-flight jobs from
    // the previous user. $reset() handles the in-memory side;
    // sessionStorage.removeItem clears what pinia-plugin-persistedstate
    // had serialized under the 'simulation' key so the next login
    // boots from a clean slate.
    try {
      const { useSimulationStore } = await import('@/stores/simulation')
      useSimulationStore().$reset()
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('simulation')
      }
    } catch {
      // Defensive: a store-import failure must never prevent logout.
    }
  }

  async function refreshSession() {
    try {
      await authApi.refreshSession()
      user.value = await authApi.getMe()
      if (user.value?.language) {
        setLocale(user.value.language)
      }
      startSessionKeepAlive()
      return true
    } catch (err: unknown) {
      // 2026-05-13 — distinguish 429 (rate-limit, transient) from
      // 401 (real auth failure). The prior catch-all nulled
      // user.value on ANY error, which meant a 429 from /refresh
      // logged the user out — symptom: change language a couple of
      // times within a minute, get "Too many attempts" toast AND
      // get redirected to /login.
      //
      // /refresh itself no longer has a rate-limit dependency
      // (see app/domains/auth/router.py), but the broader rule
      // still applies: a transient HTTP failure must not destroy
      // session state. Only a confirmed 401 (server says "this
      // refresh token is no longer valid") justifies clearing
      // user. Any other error (429, 5xx, network) leaves the
      // current session intact and returns false so the caller
      // can decide on retry / toast.
      let status: number | undefined
      if (typeof err === 'object' && err !== null) {
        const maybe = err as { response?: { status?: number } }
        status = maybe.response?.status
      }
      if (status === 401) {
        user.value = null
      }
      return false
    }
  }

  async function fetchProfile() {
    try {
      user.value = await authApi.getMe()
      if (user.value?.language) {
        setLocale(user.value.language)
      }
    } catch {
      user.value = null
    }
  }

  async function updatePreferences(prefs: Partial<UserPreferences>) {
    const updated = await authApi.updatePreferences(prefs)
    if (user.value) {
      if (updated.language) {
        user.value.language = updated.language
        localStorage.setItem('agroptimizer_lang', updated.language)
        setLocale(updated.language)
      }
      if (updated.unit_system) {
        user.value.unit_system = updated.unit_system
        localStorage.setItem('agroptimizer_units', updated.unit_system)
      }
    }
  }

  // Live language change. updatePreferences() writes the new value to
  // the DB and flips the frontend locale (setLocale + localStorage).
  // refreshSession() then re-issues the JWT so the access-token's
  // session_language claim picks up the new value -- without this,
  // backend-issued strings (LLM responses, emails enqueued in the
  // current access-token window) would lag for up to one token TTL.
  //
  // Trade-off accepted (per the file-top INV-004 note): chat threads
  // and SimulationRun rows persisted under the prior
  // (user_id, field_id, language, unit_system) tuple remain visible
  // only when querying that tuple. They are not destroyed; a future
  // change can drop language from ChatThread scoping if cross-language
  // continuity becomes a requirement.
  async function changeLanguage(lang: 'en' | 'el' | 'pt-BR' | 'es') {
    if (user.value?.language === lang) return
    await updatePreferences({ language: lang })
    await refreshSession()
  }

  async function forgotPassword(email: string) {
    await authApi.forgotPassword({ email })
  }

  async function resetPassword(token: string, newPassword: string) {
    await authApi.resetPassword({ token, new_password: newPassword })
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    await authApi.changePassword({ current_password: currentPassword, new_password: newPassword })
  }

  return {
    user, isAuthenticated, language, unitSystem,
    login, register, logout, refreshSession, fetchProfile,
    updatePreferences, changeLanguage,
    forgotPassword, resetPassword, changePassword,
    startSessionKeepAlive, stopSessionKeepAlive,
  }
})
